import { parseChannelVariable } from "components/ContractsSettings/ContractEditorLexical/channelVariable";
import {
    articleRepeatIndex,
    isArticleRepeatStorageKey,
} from "components/ContractsSettings/ContractEditorLexical/RepeatablePlugin/repeatBy";
import { getBeneficiaryIndex } from "components/ContractsSettings/ContractEditorLexical/utils";
import type { UserVariables } from "components/shared/AgreementModal";
import type { IChannel } from "containers/Channels/types";
import { removeBrackets } from "helpers/utils";
import type { SetStateAction } from "react";
import { useEffect, useState } from "react";
import type { Agreement } from "store/agreementsStore";

import { isStructuralAgreementVariableKey } from "./agreementVariableKeys";
import { getFallbackAgreementVariableValue } from "./fallbackAgreementVariable";
import type { FoundBeneficiaryVariable, FoundVariable } from "./VariablesList";

const findEditorVariables = (agreement: Agreement | null, hasMultiSignature: boolean) => {
    const foundVariables: FoundVariable[] = [];
    const beneficiariesFoundVariables: Record<number, FoundBeneficiaryVariable[]> = {};
    const articleScopedKeys = new Set<string>();
    const articlesFoundVariables: Record<number, FoundBeneficiaryVariable[]> = {};
    const variablesEl = document.querySelectorAll(".PlaygroundEditorTheme__filledVariable");
    const agreementUserVariables = Object.keys(agreement?.userVariables ?? {});

    for (const variableEl of variablesEl) {
        const variableName = variableEl.getAttribute("data-variable-name") ?? "";
        const scopedKey = variableEl.getAttribute("multi-beneficiary-variable-key") ?? "";
        if (isArticleRepeatStorageKey(scopedKey)) {
            articleScopedKeys.add(scopedKey);
            const index = articleRepeatIndex(scopedKey);
            if (index == null) continue;
            const articleVariables = articlesFoundVariables[index] ?? [];
            const alreadyListed = articleVariables.some((variable) => variable.variable === variableName);
            if (!alreadyListed) {
                articleVariables.push({
                    variable: variableName,
                    name: removeBrackets(variableName),
                    multiBeneficiaryKey: scopedKey,
                });
            }
            articlesFoundVariables[index] = articleVariables;
            continue;
        }
        const isDuplicated = foundVariables.some((variable) => variable.variable === variableName);
        const multiBeneficiaryKey =
            hasMultiSignature && agreementUserVariables.find((variable) => variable === scopedKey);
        if (!isDuplicated && !multiBeneficiaryKey) {
            foundVariables.push({
                variable: variableName,
                name: removeBrackets(variableName),
            });
        }
        if (!multiBeneficiaryKey) continue;

        const index = getBeneficiaryIndex(multiBeneficiaryKey) as number;
        const beneficiaryVariables = beneficiariesFoundVariables[index] ?? [];
        const isBeneficiaryDuplicated = beneficiaryVariables.some((variable) => variable.variable === variableName);
        if (!isBeneficiaryDuplicated) {
            beneficiaryVariables.push({
                variable: variableName,
                name: removeBrackets(variableName),
                multiBeneficiaryKey,
            });
        }
        beneficiariesFoundVariables[index] = beneficiaryVariables;
    }

    return { foundVariables, beneficiariesFoundVariables, articleScopedKeys, articlesFoundVariables };
};

const cleanDraftUserVariables = (
    prev: UserVariables | undefined,
    {
        variablesList,
        beneficiariesVariablesList,
        articleScopedKeys,
        agreement,
        hasMultiSignature,
        showOpeningBalanceInLetters,
        showInitialAdvanceInLetters,
        channels,
    }: {
        variablesList: FoundVariable[] | undefined;
        beneficiariesVariablesList: Record<number, FoundBeneficiaryVariable[]>;
        articleScopedKeys: Set<string>;
        agreement: Agreement | null;
        hasMultiSignature: boolean;
        showOpeningBalanceInLetters: boolean;
        showInitialAdvanceInLetters: boolean;
        channels: IChannel[];
    },
): UserVariables | undefined => {
    if (!variablesList) return prev;
    const userVariables = structuredClone(prev);
    if (!userVariables) return prev;

    const agreementValues = (agreement?.userVariables ?? {}) as Record<string, unknown>;
    const usedKeys = new Set(variablesList.map((variable) => variable.name));
    if (hasMultiSignature) {
        for (const beneficiaryVariables of Object.values(beneficiariesVariablesList)) {
            for (const variable of beneficiaryVariables) {
                usedKeys.add(variable.multiBeneficiaryKey);
            }
        }
    }
    articleScopedKeys.forEach((key) => usedKeys.add(key));

    for (const userVar of Object.keys(userVariables)) {
        if (isStructuralAgreementVariableKey(userVar)) continue;
        if (!usedKeys.has(userVar)) {
            delete userVariables[userVar];
        }
    }

    for (const variable of variablesList) {
        if (userVariables[variable.name] == null || userVariables[variable.name] === "") {
            userVariables[variable.name] =
                getFallbackAgreementVariableValue(variable.name, agreementValues, agreement?.signers) ?? "";
        }
        const showAmountInLetters =
            (variable.name === "advance" && showOpeningBalanceInLetters) ||
            (variable.name === "initialAdvance" && showInitialAdvanceInLetters);
        if (showAmountInLetters && agreement?.userVariables?.priceAmountInLetters) {
            const amountText = agreement.userVariables.priceAmountInLetters[variable.name];
            if (amountText) userVariables[variable.name] = amountText;
        }
        if (variable.name.startsWith("channelGroup:") && !userVariables[variable.name].length) {
            const parsed = parseChannelVariable(variable.name);
            const groupedChannelId = channels.find((channel) => channel?.compensationGroupId == parsed?.id)?.id;
            const channelVariableKey =
                groupedChannelId == null
                    ? undefined
                    : parsed?.formatTypeId == null
                      ? `channel:${groupedChannelId}`
                      : `channel:${groupedChannelId}:${parsed.formatTypeId}`;
            userVariables[variable.name] = (channelVariableKey && agreement?.userVariables?.[channelVariableKey]) || "";
        }
    }

    return { ...userVariables };
};

export const useAgreementDraftVariables = ({
    agreement,
    hasMultiSignature,
    showOpeningBalanceInLetters,
    showInitialAdvanceInLetters,
    channels,
    setUserVariables,
}: {
    agreement: Agreement | null;
    hasMultiSignature: boolean;
    showOpeningBalanceInLetters: boolean;
    showInitialAdvanceInLetters: boolean;
    channels: IChannel[];
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
}) => {
    const [variablesList, setVariablesList] = useState<FoundVariable[]>();
    const [beneficiariesVariablesList, setBeneficiariesVariablesList] = useState<
        Record<number, FoundBeneficiaryVariable[]>
    >({});
    const [articleScopedKeys, setArticleScopedKeys] = useState<Set<string>>(() => new Set());
    const [articlesVariablesList, setArticlesVariablesList] = useState<Record<number, FoundBeneficiaryVariable[]>>({});

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            const {
                foundVariables,
                beneficiariesFoundVariables,
                articleScopedKeys: scopedKeys,
                articlesFoundVariables,
            } = findEditorVariables(agreement, hasMultiSignature);
            setBeneficiariesVariablesList(beneficiariesFoundVariables);
            setArticleScopedKeys(scopedKeys);
            setArticlesVariablesList(articlesFoundVariables);
            setVariablesList(foundVariables);
        }, 300);
        return () => window.clearTimeout(timeoutId);
    }, [agreement, hasMultiSignature]);

    useEffect(() => {
        setUserVariables((prev) =>
            cleanDraftUserVariables(prev, {
                variablesList,
                beneficiariesVariablesList,
                articleScopedKeys,
                agreement,
                hasMultiSignature,
                showOpeningBalanceInLetters,
                showInitialAdvanceInLetters,
                channels,
            }),
        );
    }, [
        variablesList,
        beneficiariesVariablesList,
        articleScopedKeys,
        agreement?.userVariables,
        hasMultiSignature,
        showOpeningBalanceInLetters,
        showInitialAdvanceInLetters,
    ]);

    return { variablesList, beneficiariesVariablesList, articlesVariablesList };
};
